import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Plus, Edit2, Trash2, Eye, Search, ChevronDown, ChevronUp, Package, List, Grid, Filter, X, Save, RotateCcw, Info, AlertCircle, CheckCircle2, ShoppingCart, User, Calendar, Clock, MessageCircle, BookOpen, History, BarChart as BarChartIcon, XCircle, Users } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { createClient } from '@supabase/supabase-js';
import Sidebar from '../components/layout/Sidebar'; // Kullanıcı verilerini almak için import ediyoruz
import Navbar from '../components/layout/Navbar'; // Gerekirse güncellemeler için import ediyoruz
import MakineMontajIstatistikleri from './MakineMontajIstatistikleri'; // Yeni istatistik bileşenini import et

// Supabase istemcisini başlat
const supabaseUrl = import.meta.env.VITE_FILE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_FILE_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Initialize User Profiles Supabase client (different database)
const userProfilesUrl = import.meta.env.VITE_SUPABASE_URL;
const userProfilesKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const userProfilesSupabase = createClient(userProfilesUrl, userProfilesKey);

// Bucket adını .env'den al, yoksa varsayılan olarak "device-model-images" kullan
const BUCKET_NAME = import.meta.env.VITE_FILE_SUPABASE_BUCKET_NAME || 'files';

// Örnek Veri (Normalde API\'den gelir)
const initialDeviceModels = [
  {
    id: 'dm001',
    name: 'Endüstriyel Robot Kolu XR-5000',
    description: 'Ağır sanayi uygulamaları için yüksek hassasiyetli robot kolu.',
    category: 'Robotik Otomasyon',
    modelYear: '2023-10-15',
    createdAt: '2023-10-15T10:30:00Z',
    updatedAt: '2024-03-20T14:45:00Z',
    createdBy: 'Yönetici A',
    status: 'Aktif',
    components: [
      { productId: 'prod001', productName: 'Servo Motor 750W', quantity: 4, unit: 'adet' },
      { productId: 'prod002', productName: 'PLC Kontrol Ünitesi', quantity: 1, unit: 'adet' },
      { productId: 'prod003', productName: 'Alüminyum Profil 1m', quantity: 12, unit: 'metre' },
      { productId: 'prod004', productName: 'Sensör Kiti XY-2', quantity: 2, unit: 'set' },
    ],
    notes: 'Son versiyonda tork artırıldı.',
    image_url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI0UwRTBFMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMThweCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgZmlsbD0iIzlFOUU5RSI+Q2loYXogTW9kZWxpPC90ZXh0Pjwvc3ZnPg==',
  },
  {
    id: 'dm002',
    name: 'Plastix 400',
    description: '400 x 400 x 400 PLASTİX 400 TEİRA3D PRİNTER',
    category: '3D PRİNTER',
    modelYear: '2022-11-01',
    createdAt: '2022-11-01T09:00:00Z',
    updatedAt: '2024-05-10T11:20:00Z',
    createdBy: 'Mühendis B',
    status: 'Geliştirme Aşamasında',
    components: [
      { productId: 'prod005', productName: 'Spindle Motor 5kW', quantity: 1, unit: 'adet' },
      { productId: 'prod006', productName: 'Lineer Kızak Seti', quantity: 6, unit: 'adet' },
      { productId: 'prod007', productName: 'Kontrol Paneli 15"', quantity: 1, unit: 'adet' },
    ],
    notes: 'Teira3D Plastix 400, yüksek dayanım, düşük büzülme oranı ve pürüzsüz yüzey kalitesiyle profesyonel FDM 3D yazıcılarda sorunsuz baskı deneyimi sunar. Özellikle prototipleme, mekanik parçalar ve işlevsel modellerde tercih edilir. 1.75 mm çapında, nem geçirmez vakumlu ambalajında gönderilir. Baskı sıcaklığı 200–220°C, tabla sıcaklığı 50–60°C aralığında önerilir.',
    image_url: 'https://www.teira3d.com/assets/img/section/WebImages/10P2.webp',
  },
  // Daha fazla örnek model eklenebilir
];

// Örnek Ürün Listesi (Reçete için)
// const sampleProducts = [...]; // Bu satırı kaldıracağız veya yorumlayacağız

// --- StokYonetimi.jsx'ten Alınan Yardımcı Fonksiyonlar --- START ---
// Türkçe karakterleri normalize etme
const normalizeText = (text) => {
  if (!text) return '';
  return text.toString()
    .toLowerCase()
    .replace(/ö/g, 'o')
    .replace(/ü/g, 'u')
    .replace(/ı/g, 'i')
    .replace(/i̇/g, 'i') // büyük İ normalize edildiğinde i̇ olabiliyor
    .replace(/ş/g, 's')
    .replace(/ç/g, 'c')
    .replace(/ğ/g, 'g')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, ''); // Aksanları kaldır
};

// Bulanık arama için benzerlik puanı hesaplama
const getSimilarityScore = (source, target) => {
  if (!source || !target) return 0;
  
  const normalizedSource = normalizeText(source);
  const normalizedTarget = normalizeText(target);
  
  if (normalizedSource === normalizedTarget) return 100;
  if (normalizedSource.includes(normalizedTarget)) return 90;
  if (normalizedTarget.includes(normalizedSource)) return 80;
  
  const calculateLevenshteinDistance = (a, b) => {
    const matrix = Array(a.length + 1).fill().map(() => Array(b.length + 1).fill(0));
    for (let i = 0; i <= a.length; i++) matrix[i][0] = i;
    for (let j = 0; j <= b.length; j++) matrix[0][j] = j;
    for (let i = 1; i <= a.length; i++) {
      for (let j = 1; j <= b.length; j++) {
        const cost = a[i-1] === b[j-1] ? 0 : 1;
        matrix[i][j] = Math.min(
          matrix[i-1][j] + 1,      
          matrix[i][j-1] + 1,      
          matrix[i-1][j-1] + cost  
        );
      }
    }
    return matrix[a.length][b.length];
  };
  
  const maxLength = Math.max(normalizedSource.length, normalizedTarget.length);
  if (maxLength === 0) return 0;
  const distance = calculateLevenshteinDistance(normalizedSource, normalizedTarget);
  const similarity = 1 - (distance / maxLength);
  return Math.round(similarity * 70);
};

// Reçete ürünleri için arama skoru hesaplama
const getSearchScoreForRecipeProducts = (product, searchTerm) => {
  if (!searchTerm) return { score: 0, matches: [], matchDetails: [] };
  const term = normalizeText(searchTerm);
  const matches = [];
  let totalScore = 0;

  const fieldWeights = {
    name: 10,
    description: 5,
  };

  const nameScore = getSimilarityScore(product.name, term) * fieldWeights.name;
  if (nameScore > 0) {
    matches.push({ field: 'name', score: nameScore });
    totalScore += nameScore;
  }

  if (product.description) {
    const descScore = getSimilarityScore(product.description, term) * fieldWeights.description;
    if (descScore > 0) {
      matches.push({ field: 'description', score: descScore });
      totalScore += descScore;
    }
  }
  
  const wordCount = term.split(/\s+/).filter(Boolean).length;
  if (wordCount > 1) {
    const fullTextMatches = [product.name, product.description]
      .filter(Boolean)
      .map(field => normalizeText(field).includes(term) ? 20 : 0);
    totalScore += Math.max(...fullTextMatches, 0);
  }

  matches.sort((a, b) => b.score - a.score);
  const matchedFields = matches.map(m => m.field);

  return { 
    score: totalScore, 
    matches: matchedFields,
    matchDetails: matches 
  };
};
// --- StokYonetimi.jsx'ten Alınan Yardımcı Fonksiyonlar --- END ---

// Helper function to delete old image from Supabase Storage
const deleteOldImageFromStorage = async (imageUrl) => {
  console.log("deleteOldImageFromStorage ÇAĞRILDI, URL:", imageUrl);

  if (!imageUrl || typeof imageUrl !== 'string') {
    console.log("Geçersiz veya boş URL, silme iptal edildi.");
    return;
  }

  if (imageUrl.startsWith('data:')) {
    console.log("Base64 görsel, depodan silme işlemi gerekmiyor.");
    return;
  }

  try {
    // Başlangıç değerleri
    let filePath = null;
    let bucketName = BUCKET_NAME;
    
    // 1. Regex ile standart Supabase URL formatını yakala
    // Format: /storage/v1/object/public/<bucketName>/<filePath>
    const standardRegex = /\/storage\/v1\/object\/public\/([^\/]+)\/(.+)/;
    const standardMatch = imageUrl.match(standardRegex);
    
    if (standardMatch) {
      bucketName = standardMatch[1]; // İlk grup bucket adı
      filePath = standardMatch[2];   // İkinci grup dosya yolu
      console.log(`REGEX: URL'den bucket ve path çıkarıldı - Bucket: ${bucketName}, Path: ${filePath}`);
    }
    // 2. Özel URL formatı: files/public/filename.jpg
    else if (imageUrl.includes('/files/public/')) {
      const filesPublicRegex = /\/files\/public\/(.+)/;
      const filesMatch = imageUrl.match(filesPublicRegex);
      
      if (filesMatch) {
        bucketName = "files";
        filePath = `public/${filesMatch[1]}`;
        console.log(`FILES/PUBLIC: Özel format - Bucket: ${bucketName}, Path: ${filePath}`);
      }
    }
    // 3. Sadece dosya adı formatı (son çare olarak URL'nin son kısmını al)
    else {
      // URL'den sadece dosya adını al
      const urlParts = imageUrl.split('/');
      const fileName = urlParts[urlParts.length - 1];
      
      // Şu anda dosya nereye yükleniyorsa, oraya uygula
      filePath = `public/${fileName}`; 
      console.log(`SON ÇARE: Sadece dosya adı - Bucket: ${bucketName}, Path: ${filePath}`);
    }
    
    // Dosya yolunu çıkaramadıysak, işlemi sonlandır
    if (!filePath) {
      console.error("URL'den dosya yolu çıkarılamadı, silme işlemi iptal edildi:", imageUrl);
      return;
    }
    
    // Silme işlemi için log
    console.log(`SİLME İŞLEMİ: Bucket: ${bucketName}, Dosya yolu: ${filePath}`);
    
    // Supabase storage silme işlemi
    const { error: deleteError, data } = await supabase.storage
      .from(bucketName)
      .remove([filePath]);
    
    if (deleteError) {
      if (deleteError.message && deleteError.message.includes('Not found')) {
        console.warn(`Dosya bulunamadı: ${filePath}. Muhtemelen daha önce silinmiş veya yolu yanlış.`);
      } else {
        console.error("Dosya silme HATASI:", deleteError);
        console.error("Tam hata detayı:", JSON.stringify(deleteError));
      }
    } else {
      console.log(`BAŞARILI: "${filePath}" dosyası başarıyla silindi.`, data);
    }
  } catch (err) {
    console.error("Supabase silme işlemi sırasında beklenmeyen HATA:", err);
    console.error("Hata stack trace:", err.stack);
  }
};

const MakineMontajListesi = () => {
  const [deviceModels, setDeviceModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(true);
  const [modelsError, setModelsError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'ascending' });
  const [showFullImageModal, setShowFullImageModal] = useState(false);
  const [showAllComponents, setShowAllComponents] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showFullNotes, setShowFullNotes] = useState(false);
  
  // Arama ve filtreleme için yeni stateler
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [searchFilters, setSearchFilters] = useState({
    category: '',
    status: '',
    dateFrom: '',
    dateTo: '',
  });
  const [categoriesList, setCategoriesList] = useState([]);
  
  const [allProducts, setAllProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [componentSearchTerm, setComponentSearchTerm] = useState('');
  const [showProductSelectionModal, setShowProductSelectionModal] = useState(false);
  const [modalProductQuantities, setModalProductQuantities] = useState({});
  
  // Kullanıcı profili ve izinleri için state'ler
  const [sidebarUserProfile, setSidebarUserProfile] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  // İzinler için state
  const [permissions, setPermissions] = useState({
    makine_add_permission: false,
    makine_edit_permission: false,
    makine_delete_permission: false
  });
  const [activeTab, setActiveTab] = useState('liste'); // 'liste' veya 'istatistikler'

  const initialFormState = {
    id: null,
    name: '',
    description: '',
    category: '',
    model_year: '',
    status: 'Aktif',
    components: [],
    notes: '',
    image_url: '',
    created_by: 'Mevcut Kullanıcı'
  };
  const [currentForm, setCurrentForm] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState({});

  const [selectedFile, setSelectedFile] = useState(null); // Seçilen dosya için state
  const [uploadingImage, setUploadingImage] = useState(false); // Yükleme durumu için state
  const fileInputRef = useRef(null); // Dosya inputuna erişim için ref
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null); // Önizleme için state

  const fetchDeviceModels = useCallback(async () => {
    setModelsLoading(true);
    setModelsError(null);
    try {
      const { data, error } = await supabase
        .from('device_models')
        .select('*')
        .order(sortConfig.key, { ascending: sortConfig.direction === 'ascending' });

      if (error) throw error;
      setDeviceModels(data || []);
    } catch (err) {
      console.error("Cihaz modelleri çekilirken hata:", err);
      setModelsError('Cihaz modelleri yüklenemedi: ' + err.message);
      setDeviceModels([]);
    } finally {
      setModelsLoading(false);
    }
  }, [sortConfig]);

  useEffect(() => {
    fetchDeviceModels();
  }, [fetchDeviceModels]);

  useEffect(() => {
    const fetchProductsFromStock = async () => {
      setProductsLoading(true);
      try {
        const { data, error } = await supabase
          .from('products')
          .select('id, name, stock, image_url, description');
        if (error) throw error;
        setAllProducts(data || []);
      } catch (err) {
        console.error('Stok ürünleri çekme hatası:', err);
        setAllProducts([]);
      } finally {
        setProductsLoading(false);
      }
    };
    fetchProductsFromStock();
  }, []);

  useEffect(() => {
    // Form açıldığında veya currentForm değiştiğinde preview'ı ayarla
    if (currentForm.image_url) {
      setImagePreviewUrl(currentForm.image_url);
    } else {
      setImagePreviewUrl(null);
    }
    // Seçili dosyayı temizle (form her açıldığında)
    setSelectedFile(null);
    // Dosya inputunu temizle
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Cleanup function for object URL
    return () => {
      if (imagePreviewUrl && imagePreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFormModal, currentForm.image_url]); // Sadece modal açıldığında ve image_url değiştiğinde çalışsın

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentForm({ ...currentForm, [name]: value });
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: null });
    }
  };

  const handleComponentChange = (index, field, value) => {
    const updatedComponents = [...currentForm.components];
    
    // For quantity fields, ensure we have a valid string value
    if (field === 'quantity') {
      if (value === null || value === undefined) {
        value = '';
      }
      // Handle negative values
      if (parseFloat(value) < 0) {
        value = '0';
      }
    } else if (value === null || value === undefined) {
      // For other fields like unit, ensure no null values
      value = '';
    }
    
    updatedComponents[index][field] = value;
    setCurrentForm({ ...currentForm, components: updatedComponents });
  };

  const handleModalQuantityChange = (productId, value) => {
    const quantity = Math.max(1, parseInt(value || '1') || 1);
    setModalProductQuantities(prev => ({ ...prev, [productId]: quantity }));
  };

  const addComponent = (product, quantity) => {
    if (!product) return;
    if (currentForm.components.find(c => c.productId === product.id)) return;

    // Ensure quantity is always a valid string or number, never null
    const safeQuantity = quantity === null || quantity === undefined ? 1 : quantity;

    setCurrentForm(prevForm => ({
      ...prevForm,
      components: [...prevForm.components, {
        productId: product.id,
        productName: product.name,
        quantity: safeQuantity,
        unit: 'adet'
      }],
    }));
  };

  const removeComponentByProductId = (productIdToRemove) => {
    setCurrentForm(prevForm => ({
      ...prevForm,
      components: prevForm.components.filter(c => c.productId !== productIdToRemove)
    }));
  };

  const validateForm = () => {
    const errors = {};
    if (!currentForm.name.trim()) errors.name = 'Cihaz adı gerekli.';
    if (!currentForm.category.trim()) errors.category = 'Kategori gerekli.';
    if (!currentForm.model_year) errors.model_year = 'Model yılı gerekli.';
    if (currentForm.components.length === 0) errors.components = 'En az bir bileşen eklenmeli.';
    currentForm.components.forEach((comp, index) => {
      if (comp.quantity === '' || isNaN(parseFloat(comp.quantity)) || parseFloat(comp.quantity) <= 0) {
        if (!errors.componentQuantities) errors.componentQuantities = {};
        errors.componentQuantities[index] = 'Geçerli bir miktar girin.';
      }
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Dosya boyut kontrolü (2MB)
      if (file.size > 2 * 1024 * 1024) {
        setModelsError("Dosya boyutu 2MB'ı aşamaz");
        return;
      }
      
      setSelectedFile(file);
      // Mevcut blob URL varsa temizle
      if (imagePreviewUrl && imagePreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
      // Yeni önizleme URL'si oluştur
      setImagePreviewUrl(URL.createObjectURL(file));
      // Görsel URL input'unu temizle (dosya seçildi)
      setCurrentForm(prev => ({...prev, image_url: ''}));
    } else {
      setSelectedFile(null);
      // Dosya seçimi iptal edilirse, varsa mevcut URL'ye geri dön veya temizle
      setImagePreviewUrl(currentForm.image_url || null);
    }
  };

  // URL input'u değiştiğinde dosyı ve önizlemeyi temizle
  const handleImageUrlChange = (e) => {
    const { name, value } = e.target;
    setCurrentForm({ ...currentForm, [name]: value });
    if (value) { // Eğer URL giriliyorsa, dosya seçimini iptal et
      setSelectedFile(null);
       // Mevcut blob URL varsa temizle
      if (imagePreviewUrl && imagePreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
      setImagePreviewUrl(value); // Inputtaki URL'yi preview olarak ayarla
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } else { // URL temizlenirse preview'ı da temizle
       setImagePreviewUrl(null);
    }
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: null });
    }
  };

  // GÜNCELLENDİ: Görseli Kaldırma ve Fiziksel Silme
  const handleRemoveImage = async () => {
    // console.log("handleRemoveImage ÇAĞRILDI - Görseli Kaldır butonuna tıklandı");
    const imageUrlToRemove = currentForm.image_url;
    // console.log("Kaldırılacak görsel URL:", imageUrlToRemove);

    setSelectedFile(null);
    setCurrentForm(prev => ({...prev, image_url: ''}));
    // Mevcut blob URL varsa temizle
    if (imagePreviewUrl && imagePreviewUrl.startsWith('blob:')) {
      // console.log("Blob URL serbest bırakılıyor:", imagePreviewUrl);
      URL.revokeObjectURL(imagePreviewUrl);
    }
    setImagePreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // State güncellendikten sonra, eğer varsa ve base64 değilse, eski görseli storage'dan sil
    if (imageUrlToRemove && !imageUrlToRemove.startsWith('data:')) {
      // console.log("Eski görseli storage'dan silme işlemi başlatılıyor...");
      try {
        // Silme işlemini garantiye almak için timeout kullan
        setTimeout(async () => {
          await deleteOldImageFromStorage(imageUrlToRemove);
          // console.log("Silme işlemi tamamlandı (handleRemoveImage - timeout içinde)");
        }, 500);
        // console.log("Silme işlemi zamanlandı (handleRemoveImage)");
      } catch (err) {
        console.error("handleRemoveImage silme işleminde HATA:", err);
      }
    } else {
      // console.log("Silinecek eski görsel URL'si yok veya base64 görsel, işlem atlanıyor");
    }
  };

  const handleSubmit = async () => {
    // İzin kontrolü
    if (!permissions.makine_add_permission) {
      setModelsError('Cihaz modeli ekleme yetkiniz bulunmamaktadır.');
      return;
    }
    
    if (!validateForm()) return;
    setModelsLoading(true);
    setUploadingImage(false);
    setModelsError(null);

    let finalImageUrl = currentForm.image_url || null;
    const oldImageUrl = isEditing && selectedModel ? selectedModel.image_url : null;

    try {
      // 1. Dosya Yükleme (eğer varsa)
      if (selectedFile) {
        setUploadingImage(true);
        const fileExt = selectedFile.name.split('.').pop();
        const fileName = `${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `public/${fileName}`;

        const { data: uploadData, error: uploadError } = await supabase.storage
          .from(BUCKET_NAME)
          .upload(filePath, selectedFile, {
            cacheControl: '3600',
            upsert: false,
          });

        setUploadingImage(false);
        if (uploadError) {
          throw new Error(`Dosya yüklenemedi: ${uploadError.message}`);
        }

        const { data: urlData } = supabase.storage
          .from(BUCKET_NAME)
          .getPublicUrl(filePath);

        finalImageUrl = urlData?.publicUrl || null;
         if (!finalImageUrl) {
            // console.warn('Dosya yüklendi ancak public URL alınamadı.', uploadData);
        }
      }

      // Kullanıcı bilgilerini al - StokProductCreate.jsx ile aynı mantığı kullan
      let currentUserName = "Sistem Kullanıcısı";
      
      // Öncelikle Sidebar'dan gelen kullanıcı bilgisini kontrol et
      if (sidebarUserProfile) {
        currentUserName = `${sidebarUserProfile.first_name} ${sidebarUserProfile.last_name}`;
      } else {
        // Yoksa Supabase auth'tan almayı dene
        try {
      const { data: { user } } = await supabase.auth.getUser();
          currentUserName = user?.user_metadata?.full_name || user?.email || currentUserName;
        } catch (authError) {
          console.error("Kullanıcı bilgisi alınamadı:", authError);
        }
      }

      // 2. Veritabanı Kayıt/Güncelleme
      const modelData = {
        ...currentForm,
        image_url: finalImageUrl,
        updated_at: new Date().toISOString(),
        model_year: currentForm.model_year || null
      };

      let dbError = null;
      if (isEditing && selectedModel) {
        modelData.updated_by = currentUserName; // Kullanıcı adını güncelleme kaydına ekle
        const { error } = await supabase
          .from('device_models')
          .update(modelData)
          .eq('id', selectedModel.id);
         dbError = error;
      } else {
        modelData.id = `dm_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
        modelData.created_at = new Date().toISOString();
        modelData.created_by = currentUserName; // Kullanıcı adını oluşturma kaydına ekle
        modelData.updated_by = currentUserName; // İlk kayıtta güncelleyen de aynı kişi
        const { error } = await supabase
          .from('device_models')
          .insert(modelData);
        dbError = error;
      }

      if (dbError) {
          throw dbError;
      }

      // 3. Eski Görseli Sil (SADECE DÜZENLEME, BAŞARILI DB GÜNCELLEME sonrası ve URL farklıysa)
      if (isEditing && oldImageUrl && oldImageUrl !== finalImageUrl) {
          // console.log("GÜNCELLEME: Eski ve yeni URL'ler farklı, eski görseli silme işlemi başlatılıyor");
          // console.log("Eski URL:", oldImageUrl);
          // console.log("Yeni URL:", finalImageUrl);
          try {
              // Dosyayı silme işlemini garantiye almak için timeout ekle
              setTimeout(async () => {
                  await deleteOldImageFromStorage(oldImageUrl);
                  // console.log("Silme işlemi tamamlandı (handleSubmit - timeout içinde)");
              }, 500);
              // console.log("Silme işlemi zamanlandı (handleSubmit)");
          } catch (err) {
              console.error("handleSubmit içinde eski görsel silme HATASI:", err);
          }
      }

      // 4. Başarı Durumu
      setShowFormModal(false);
      setCurrentForm(initialFormState);
      setSelectedFile(null);
      setImagePreviewUrl(null);
      setIsEditing(false);
      setSelectedModel(null);
      await fetchDeviceModels();

    } catch (err) {
      console.error("Cihaz modeli kaydedilirken/güncellenirken hata:", err);
      if (err.message.includes('Dosya yüklenemedi')) {
          setModelsError(err.message);
      } else {
          setModelsError(`Veritabanı işlemi başarısız: ${err.message}`);
      }
      setUploadingImage(false);
    } finally {
      setModelsLoading(false);
    }
  };

  const handleAddNew = () => {
    // İzin kontrolü
    if (!permissions.makine_add_permission) {
      setModelsError('Cihaz modeli ekleme yetkiniz bulunmamaktadır.');
      return;
    }
    
    // Diğer modalları kapat
    setShowDetailModal(false);
    setShowDeleteModal(false);
    
    setIsEditing(false);
    setCurrentForm(initialFormState);
    setFormErrors({});
    setShowFormModal(true);
  };

  const handleEdit = (model) => {
    // Diğer modalları kapat
    setShowDetailModal(false);
    setShowDeleteModal(false);
    
    setSelectedModel(model);
    setIsEditing(true);
    setCurrentForm({ ...model, model_year: model.model_year ? model.model_year.split('T')[0] : '' });
    setFormErrors({});
    setShowFormModal(true);
  };

  const handleViewDetails = (model) => {
    // Diğer modalları kapat
    setShowDetailModal(false);
    setShowDeleteModal(false);
    
    setSelectedModel(model);
    setShowDetailModal(true);
  };

  const handleDelete = (model) => {
    // Diğer modalları kapat
    setShowDetailModal(false);
    setShowFormModal(false);
    
    setSelectedModel(model);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedModel) return;

    const modelToDelete = { ...selectedModel }; // Silme işleminden önce model bilgilerini kopyala

    setModelsLoading(true);
    setModelsError(null);
    try {
      // 1. Veritabanından cihaz modelini sil
      const { error: dbError } = await supabase
        .from('device_models')
        .delete()
        .eq('id', modelToDelete.id);

      if (dbError) {
        console.error("Veritabanından silme hatası:", dbError);
        throw new Error(`Veritabanı kaydı silinemedi: ${dbError.message}`);
      }
      // console.log(`Veritabanı kaydı başarıyla silindi: ${modelToDelete.id}`);

      // 2. Veritabanı silme işlemi başarılıysa, Supabase Storage'dan görseli sil
      if (modelToDelete.image_url) {
        // console.log(`İlişkili görseli (${modelToDelete.image_url}) Supabase Storage'dan silme denemesi...`);
        
        // Silme işlemini garantiye almak için timeout kullan
        setTimeout(async () => {
          try {
            await deleteOldImageFromStorage(modelToDelete.image_url);
            // console.log("Silme işlemi tamamlandı (confirmDelete - timeout içinde)");
          } catch (err) {
            console.error("Timeout içindeki silme işlemi HATASI:", err);
          }
        }, 500);
        // console.log("Silme işlemi zamanlandı (confirmDelete)");
      } else {
        // console.log(`Silinen model (${modelToDelete.id}) için Supabase'te silinecek bir görsel URL'si bulunmuyor.`);
      }

      setShowDeleteModal(false);
      setSelectedModel(null);
      await fetchDeviceModels(); // Listeyi yenile

    } catch (err) {
      console.error("Cihaz modeli silinirken genel hata:", err);
      setModelsError(`Cihaz modeli silinemedi: ${err.message}`);
      // fetchDeviceModels kendi içinde modelsLoading'i false yapar.
    } finally {
      // Her durumda (başarılı veya hatalı) loading durumunu false yapalım.
      // Ancak fetchDeviceModels bunu zaten yaptığı için, eğer orası kesin çalışıyorsa bu satır gereksiz olabilir.
      // Garanti olması için burada da setModelsLoading(false) çağrılabilir.
      // Ancak mevcut kodda fetchDeviceModels() zaten setLoading(false) yapıyor.
      // Bu yüzden buradaki finally bloğunda ekstradan setModelsLoading(false) yapmaya gerek yok.
      // Eğer sadece error durumunda loading'i false yapmak isteseydik, catch bloğuna koyardık.
      // fetchDeviceModels başarılı olursa o zaten false yapacak.
      // Ancak, confirmDelete'in kendi loading state'i varsa (ki var: modelsLoading), onu burada yönetmek daha doğru.
      setModelsLoading(false); 
    }
  };

  // Filtreleri uygulama fonksiyonu
  const applyFilters = (models) => {
    if (!models) return [];
    
    return models.filter(model => {
      // Temel metin araması
      const basicSearchMatch = (
    (model.name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (model.category?.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (model.description?.toLowerCase().includes(searchTerm.toLowerCase()))
  );
      
      if (!basicSearchMatch) return false;
      
      // Kategori filtresi
      if (searchFilters.category && model.category !== searchFilters.category) {
        return false;
      }
      
      // Durum filtresi
      if (searchFilters.status && model.status !== searchFilters.status) {
        return false;
      }
      
      // Tarih filtresi
      if (searchFilters.dateFrom) {
        const modelDate = new Date(model.updated_at || model.created_at);
        const fromDate = new Date(searchFilters.dateFrom);
        if (modelDate < fromDate) return false;
      }
      
      if (searchFilters.dateTo) {
        const modelDate = new Date(model.updated_at || model.created_at);
        const toDate = new Date(searchFilters.dateTo);
        // Son güne kadar dahil etmek için 23:59:59'a ayarla
        toDate.setHours(23, 59, 59);
        if (modelDate > toDate) return false;
      }
      
      return true;
    });
  };

  // Benzersiz kategorileri bulma
  useEffect(() => {
    if (deviceModels.length > 0) {
      const categories = [...new Set(deviceModels.map(model => model.category))].filter(Boolean);
      setCategoriesList(categories);
    }
  }, [deviceModels]);

  // Filtreleri sıfırlama
  const resetFilters = () => {
    setSearchFilters({
      category: '',
      status: '',
      dateFrom: '',
      dateTo: '',
    });
    setSearchTerm('');
  };

  // Filtreleri güncelleme
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setSearchFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Filtrelenmiş modeller
  const filteredDeviceModels = useMemo(() => {
    return applyFilters(deviceModels);
  }, [deviceModels, searchTerm, searchFilters]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };
  
  const filteredProductsForSelector = useMemo(() => {
    const productsWithStatus = allProducts.map(p => ({
      ...p,
      isAdded: currentForm.components.some(comp => comp.productId === p.id),
      currentQuantityInRecipe: currentForm.components.find(comp => comp.productId === p.id)?.quantity || 0
    }));

    if (!componentSearchTerm.trim()) {
      return productsWithStatus.sort((a,b) => a.name.localeCompare(b.name));
    }
    const MIN_SCORE_THRESHOLD = 5;
    const scoredProducts = productsWithStatus.map(product => {
      const searchResults = getSearchScoreForRecipeProducts(product, componentSearchTerm);
      return {
        ...product,
        searchScore: searchResults.score,
        searchMatches: searchResults.matches,
      };
    }).filter(product => product.searchScore >= MIN_SCORE_THRESHOLD);
    scoredProducts.sort((a, b) => b.searchScore - a.searchScore);
    return scoredProducts;
  }, [allProducts, componentSearchTerm, currentForm.components]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };
  
  const formatModelYear = (dateString) => {
    if (!dateString) return '-';
    try {
        return new Date(dateString).toLocaleDateString('tr-TR', {
            day: '2-digit', month: '2-digit', year: 'numeric'
        });
    } catch (e) {
        return dateString;
    }
  };
  
  const scrollbarStyles = `
    .modal-scrollbar::-webkit-scrollbar {
      width: 8px;
    }
    .modal-scrollbar::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 10px;
    }
    .modal-scrollbar::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 10px;
    }
    .modal-scrollbar::-webkit-scrollbar-thumb:hover {
      background: #555;
    }
    .dark .modal-scrollbar::-webkit-scrollbar-track {
      background: #2d3748;
    }
    .dark .modal-scrollbar::-webkit-scrollbar-thumb {
      background: #4a5568;
    }
    .dark .modal-scrollbar::-webkit-scrollbar-thumb:hover {
      background: #718096;
    }
  `;

  // Sidebar'dan kullanıcı profilini almak için fonksiyon
  const handleUserProfile = (profile) => {
    setSidebarUserProfile(profile);
  };

  // Kullanıcı profilini, rolünü ve izinlerini çek
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const { data: { user } } = await userProfilesSupabase.auth.getUser();
        if (user) {
          const { data: userProfile } = await userProfilesSupabase
            .from('user_profiles')
            .select('*, first_name, last_name, role, stok_add_permission, stok_edit_permission, stok_delete_permission')
            .eq('id', user.id)
            .single();
          
          if (userProfile) {
            setCurrentUser(userProfile);
            setUserRole(userProfile?.role);
            
            // İzinleri ayarla
            setPermissions({
              makine_add_permission: Boolean(userProfile.stok_add_permission),
              makine_edit_permission: Boolean(userProfile.stok_edit_permission),
              makine_delete_permission: Boolean(userProfile.stok_delete_permission)
            });
          }
        }
      } catch (error) {
        console.error('Kullanıcı bilgileri alınırken hata:', error);
      }
    };
    getCurrentUser();
  }, []);

  if (modelsLoading && deviceModels.length === 0 && activeTab === 'liste') { // Sadece liste görünümündeyken yükleme ekranı
    return <div className="flex justify-center items-center h-screen text-xl">Cihaz Modelleri Yükleniyor...</div>;
  }
  if (modelsError && deviceModels.length === 0 && activeTab === 'liste') { // Sadece liste görünümündeyken hata ekranı
    return <div className="flex flex-col justify-center items-center h-screen text-red-500">
        <p className="text-xl">Hata: {modelsError}</p>
        <button onClick={fetchDeviceModels} className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">Tekrar Dene</button>
    </div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Sadece kullanıcı verileri için görünmez Sidebar */}
      <div className="hidden">
        <Sidebar onUserProfileUpdate={handleUserProfile} />
      </div>
      
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <style>{scrollbarStyles}</style>
        
        {/* Hata ve yükleme bildirimleri (istatistikler sayfası için ayrı kontrol gerekebilir) */}
        {modelsError && !showFormModal && !showDeleteModal && activeTab === 'liste' && (
        <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-md dark:bg-red-900/30 dark:text-red-300 dark:border-red-700">
            <p><strong>Hata:</strong> {modelsError} <button onClick={() => setModelsError(null)} className="ml-2 text-sm font-semibold hover:underline">Kapat</button></p>
        </div>
      )}
        
        {modelsLoading && activeTab === 'liste' && (
        <div className="fixed top-4 right-4 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg z-[100]">
            Yükleniyor...
        </div>
      )}
        
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex flex-col sm:flex-row justify-between items-center mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <Package size={32} className="text-blue-600 dark:text-blue-400" />
            <h1 className="text-3xl font-bold text-gray-800 dark:text-white ml-3">
                {activeTab === 'liste' ? 'Makine Montaj Listesi' : 'Montaj İstatistikleri'}
            </h1>
          </div>
            {activeTab === 'liste' && (
              <div className="mt-4 sm:mt-0">
                {permissions.makine_add_permission ? (
                  <button
                    onClick={handleAddNew}
                    className="flex items-center bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-150 ease-in-out"
                  >
                    <Plus size={20} className="mr-2" />
                    Yeni Cihaz Modeli Ekle
                  </button>
                ) : (
                  <div className="flex items-center gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
                    <XCircle className="w-5 h-5" />
                    <span className="text-sm font-medium">Ekleme Yetkiniz Yok</span>
                  </div>
                )}
              </div>
            )}
        </div>

        {/* İzin yoksa uyarı mesajı */}
        {activeTab === 'liste' && !permissions.makine_add_permission && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl shadow-sm flex items-start">
            <AlertCircle className="w-6 h-6 text-red-500 dark:text-red-400 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-medium text-red-800 dark:text-red-300 mb-1">
                Cihaz Modeli Ekleme Yetkiniz Bulunmuyor
              </h3>
              <p className="text-red-700 dark:text-red-400">
                Cihaz modeli ekleme yetkiniz bulunmamaktadır. Bu işlem için gerekli izinlere sahip değilsiniz. 
                Yetki talep etmek için sistem yöneticinizle iletişime geçiniz.
              </p>
              <div className="mt-3 flex items-center gap-2 text-sm">
                <User className="w-4 h-4 text-red-500 dark:text-red-400" />
                <span className="text-red-700 dark:text-red-400">
                  Kullanıcı: {currentUser?.first_name} {currentUser?.last_name}
                </span>
                <span className="mx-2 text-red-300 dark:text-red-700">|</span>
                <Users className="w-4 h-4 text-red-500 dark:text-red-400" />
                <span className="text-red-700 dark:text-red-400">
                  Rol: {userRole === 'super' ? 'Süper Yönetici' : userRole === 'admin' ? 'Yönetici' : 'Standart Kullanıcı'}
                </span>
              </div>
            </div>
          </div>
        )}

          {/* Sekme Butonları */}
          <div className="mb-6 flex justify-center space-x-2 border-b border-gray-200 dark:border-gray-700">
            <button 
              onClick={() => setActiveTab('liste')} 
              className={`px-6 py-3 font-medium text-sm leading-5 rounded-t-lg focus:outline-nonetransition-colors duration-150
                ${
                  activeTab === 'liste' 
                    ? 'border-blue-500 border-b-2 text-blue-600 dark:text-blue-400' 
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
            >
              <List size={18} className="inline mr-1.5"/> Cihaz Listesi
            </button>
            <button 
              onClick={() => setActiveTab('istatistikler')} 
              className={`px-6 py-3 font-medium text-sm leading-5 rounded-t-lg focus:outline-none transition-colors duration-150
                ${
                  activeTab === 'istatistikler' 
                    ? 'border-blue-500 border-b-2 text-blue-600 dark:text-blue-400' 
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
            >
              <BarChartIcon size={18} className="inline mr-1.5"/> İstatistikler
            </button>
          </div>

          {activeTab === 'liste' && (
            <>
              {/* Arama ve Filtreleme Bloğu - Önceden oluşturulduğu gibi kalacak */}
              <div className="mb-6 bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="relative flex items-center">
                    <div className="flex-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                      </div>
            <input
              type="text"
              placeholder="Model adı, kategori veya açıklamada ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
                        className="block w-full pl-10 pr-12 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 transition-shadow"
                      />
                      {searchTerm && (
                        <button
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          onClick={() => setSearchTerm('')}
                        >
                          <X className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300" />
                        </button>
                      )}
          </div>

                    <div className="flex items-center ml-4">
                      <button
                        onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
                        className={`flex items-center px-3 py-2 rounded-lg border ${
                          showAdvancedSearch 
                            ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300' 
                            : 'border-gray-300 hover:bg-gray-50 text-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700/50'
                        }`}
                      >
                        <Filter size={18} className="mr-1.5" />
                        <span className="hidden sm:inline">Filtreler</span>
                        {showAdvancedSearch ? (
                          <ChevronUp size={18} className="ml-1" />
                        ) : (
                          <ChevronDown size={18} className="ml-1" />
                        )}
                      </button>
                    </div>

                    <div className="ml-4 flex items-center space-x-2 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg">
            <button
              onClick={() => setViewMode('list')}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'list' ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-300 shadow' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600/[0.5]'
              }`}
            >
                        <List size={18} className="inline sm:mr-1.5" /> <span className="hidden sm:inline">Liste</span>
            </button>
            <button
              onClick={() => setViewMode('grid')}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'grid' ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-300 shadow' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600/[0.5]'
              }`}
            >
                        <Grid size={18} className="inline sm:mr-1.5" /> <span className="hidden sm:inline">Kart</span>
            </button>
          </div>
        </div>
        
                  {/* Gelişmiş Arama Paneli */}
        <AnimatePresence>
                    {showAdvancedSearch && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="pt-4 mt-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Kategori</label>
                            <select
                              name="category"
                              value={searchFilters.category}
                              onChange={handleFilterChange}
                              className="block w-full pl-3 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="">Tümü</option>
                              {categoriesList.map(category => (
                                <option key={category} value={category}>{category}</option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Durum</label>
                            <select
                              name="status"
                              value={searchFilters.status}
                              onChange={handleFilterChange}
                              className="block w-full pl-3 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="">Tümü</option>
                              <option value="Aktif">Aktif</option>
                              <option value="Geliştirme Aşamasında">Geliştirme Aşamasında</option>
                              <option value="Pasif">Pasif</option>
                              <option value="Arşivlendi">Arşivlendi</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Başlangıç Tarihi</label>
                            <input
                              type="date"
                              name="dateFrom"
                              value={searchFilters.dateFrom}
                              onChange={handleFilterChange}
                              className="block w-full pl-3 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Bitiş Tarihi</label>
                            <input
                              type="date"
                              name="dateTo"
                              value={searchFilters.dateTo}
                              onChange={handleFilterChange}
                              className="block w-full pl-3 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>

                        <div className="mt-4 flex justify-end">
                          <button
                            onClick={resetFilters}
                            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg mr-3 flex items-center"
                          >
                            <RotateCcw size={16} className="mr-1.5" /> Sıfırla
                          </button>
                          <button
                            onClick={() => setShowAdvancedSearch(false)}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center"
                          >
                            <Search size={16} className="mr-1.5" /> Filtrele ({filteredDeviceModels.length} sonuç)
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                
                {/* Aktif Filtreler Göstergesi */}
                {(searchFilters.category || searchFilters.status || searchFilters.dateFrom || searchFilters.dateTo) && (
                  <div className="px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border-t border-blue-100 dark:border-blue-800 flex flex-wrap items-center gap-2">
                    <span className="text-xs font-medium text-blue-700 dark:text-blue-300">Aktif Filtreler:</span>
                    
                    {searchFilters.category && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200">
                        Kategori: {searchFilters.category}
                        <X 
                          size={14} 
                          className="ml-1 cursor-pointer"
                          onClick={() => setSearchFilters(prev => ({ ...prev, category: '' }))}
                        />
                      </span>
                    )}
                    
                    {searchFilters.status && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200">
                        Durum: {searchFilters.status}
                        <X 
                          size={14} 
                          className="ml-1 cursor-pointer"
                          onClick={() => setSearchFilters(prev => ({ ...prev, status: '' }))}
                        />
                      </span>
                    )}
                    
                    {searchFilters.dateFrom && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200">
                        Başlangıç: {new Date(searchFilters.dateFrom).toLocaleDateString('tr-TR')}
                        <X 
                          size={14} 
                          className="ml-1 cursor-pointer"
                          onClick={() => setSearchFilters(prev => ({ ...prev, dateFrom: '' }))}
                        />
                      </span>
                    )}
                    
                    {searchFilters.dateTo && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200">
                        Bitiş: {new Date(searchFilters.dateTo).toLocaleDateString('tr-TR')}
                        <X 
                          size={14} 
                          className="ml-1 cursor-pointer"
                          onClick={() => setSearchFilters(prev => ({ ...prev, dateTo: '' }))}
                        />
                      </span>
                    )}
                    
                    <button
                      onClick={resetFilters}
                      className="ml-auto text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                    >
                      Tümünü Temizle
                    </button>
                  </div>
                )}
              </div>

              {/* GÜNCELLEME: filteredDeviceModels değişkenini kullan */}
              <AnimatePresence>
                {filteredDeviceModels.length === 0 && !searchTerm && !searchFilters.category && !searchFilters.status && !searchFilters.dateFrom && !searchFilters.dateTo && (
             <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center py-12"
              >
                <Package size={64} className="mx-auto text-gray-400 dark:text-gray-500 mb-4" />
                <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2 animate-pulse">Henüz Cihaz Modeli Eklenmemiş</h2>
                <p className="text-gray-500 dark:text-gray-400 mb-6">
                  Başlamak için 'Yeni Cihaz Modeli Ekle' butonuna tıklayın.
                </p>
              </motion.div>
          )}
                {filteredDeviceModels.length === 0 && (searchTerm || searchFilters.category || searchFilters.status || searchFilters.dateFrom || searchFilters.dateTo) && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-12"
            >
              <Search size={64} className="mx-auto text-gray-400 dark:text-gray-500 mb-4" />
                    <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2 animate-pulse">Arama Sonucu Bulunamadı</h2>
                    <p className="text-gray-500 dark:text-gray-400 mb-2">
                      Aktif filtrelere veya arama terimine uygun sonuç bulunamadı.
                    </p>
                    <button 
                      onClick={resetFilters}
                      className="mt-4 px-4 py-2 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/30 dark:hover:bg-blue-800/50 text-blue-700 dark:text-blue-300 rounded-lg inline-flex items-center text-sm font-medium"
                    >
                      <RotateCcw size={16} className="mr-1.5" /> Filtreleri Temizle
                    </button>
            </motion.div>
          )}
        </AnimatePresence>

        {viewMode === 'list' && filteredDeviceModels.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ staggerChildren: 0.1 }}
            className="bg-white dark:bg-gray-800 shadow-xl rounded-xl overflow-hidden"
          >
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    {['Model Adı', 'Kategori', 'Model Yılı', 'Durum', 'Son Güncelleme', 'İşlemler'].map((header) => (
                      <th
                        key={header}
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => {
                            let keyToSort = '';
                            if (header === 'Model Adı') keyToSort = 'name';
                            else if (header === 'Kategori') keyToSort = 'category';
                            else if (header === 'Model Yılı') keyToSort = 'model_year';
                            else if (header === 'Durum') keyToSort = 'status';
                            else if (header === 'Son Güncelleme') keyToSort = 'updated_at';
                            if (keyToSort) requestSort(keyToSort);
                        }}
                      >
                        <div className="flex items-center">
                          {header}
                          {sortConfig.key === (header === 'Model Adı' ? 'name' : header === 'Kategori' ? 'category' : header === 'Model Yılı' ? 'model_year' : header === 'Durum' ? 'status' : 'updated_at') && (
                            sortConfig.direction === 'ascending' ? <ChevronUp size={14} className="ml-1.5" /> : <ChevronDown size={14} className="ml-1.5" />
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredDeviceModels.map((model) => (
                    <motion.tr
                      key={model.id}
                      onClick={() => handleViewDetails(model)}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors duration-150"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <img 
                            className="h-10 w-10 rounded-md object-cover mr-3 flex-shrink-0 bg-gray-200 dark:bg-gray-700"
                            src={model.image_url || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBmaWxsPSIjRTBFMEUwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSI4cHgiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIGZpbGw9IiM5RTlFOUUiPkNpaGF6PC90ZXh0Pjwvc3ZnPg=='} 
                            alt={model.name} 
                          />
                          <div>
                            <div className="text-sm font-semibold text-gray-900 dark:text-white">{model.name}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">{model.description}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{model.category}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                          {formatModelYear(model.model_year)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          model.status === 'Aktif' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
                          model.status === 'Geliştirme Aşamasında' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
                          'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                        }`}>
                          {model.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{formatDate(model.updated_at)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center space-x-2">
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation(); // Olayın satıra yayılmasını engelle
                                    handleViewDetails(model);
                                  }} 
                                  className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 p-1.5 rounded-full hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors" 
                                  title="Görüntüle"
                                >
                            <Eye size={18} />
                          </button>
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation(); // Olayın satıra yayılmasını engelle
                                    handleEdit(model);
                                  }} 
                                  className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 p-1.5 rounded-full hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors" 
                                  title="Düzenle"
                                >
                            <Edit2 size={18} />
                          </button>
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation(); // Olayın satıra yayılmasını engelle
                                    handleDelete(model);
                                  }} 
                                  className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 p-1.5 rounded-full hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors" 
                                  title="Sil"
                                >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {viewMode === 'grid' && filteredDeviceModels.length > 0 && (
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
                visible: { transition: { staggerChildren: 0.07 } }
            }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6"
          >
            {filteredDeviceModels.map((model) => (
              <motion.div
                key={model.id}
                variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: { opacity: 1, y: 0 }
                }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-shadow duration-300 flex flex-col cursor-pointer"
                onClick={() => handleViewDetails(model)}
              >
                <img
                  src={model.image_url || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI0UwRTBFMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMThweCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgZmlsbD0iIzlFOUU5RSI+Q2loYXogTW9kZWxpPC90ZXh0Pjwvc3ZnPg=='}
                  alt={model.name}
                  className="w-full h-48 object-cover bg-gray-200 dark:bg-gray-700"
                />
                <div className="p-5 flex flex-col flex-grow">
                  <div className="flex justify-between items-start mb-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white leading-tight">{model.name}</h3>
                    <span className={`mt-1 px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full whitespace-nowrap ${
                      model.status === 'Aktif' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
                      model.status === 'Geliştirme Aşamasında' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
                      'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                    }`}>
                      {model.status}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Kategori: {model.category} | Model Yılı: {formatModelYear(model.model_year)}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 line-clamp-2 flex-grow">{model.description}</p>
                  <div className="text-xs text-gray-400 dark:text-gray-500 mb-4">
                    Son Güncelleme: {formatDate(model.updated_at)}
                  </div>
                  <div className="mt-auto pt-3 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-2">
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewDetails(model);
                            }} 
                            className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 p-2 rounded-full hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors" 
                            title="Görüntüle"
                          >
                        <Eye size={20} />
                    </button>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(model);
                            }} 
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 p-2 rounded-full hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors" 
                            title="Düzenle"
                          >
                        <Edit2 size={20} />
                    </button>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(model);
                            }} 
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 p-2 rounded-full hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors" 
                            title="Sil"
                          >
                        <Trash2 size={20} />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
            </>
          )}

          {activeTab === 'istatistikler' && (
            <MakineMontajIstatistikleri deviceModels={deviceModels} allProducts={allProducts} />
          )}

      </motion.div>
      </div>

      {/* Modals - Sadece liste görünümünde aktif olmalı (opsiyonel) */}
      {activeTab === 'liste' && (
        <>
      <AnimatePresence>
        {showFormModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowFormModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y:20 }}
              animate={{ scale: 1, opacity: 1, y:0 }}
              exit={{ scale: 0.9, opacity: 0, y:20 }}
              transition={{type: "spring", stiffness: 300, damping: 30}}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {isEditing ? 'Cihaz Modelini Düzenle' : 'Yeni Cihaz Modeli Ekle'}
                </h2>
                <button
                  onClick={() => setShowFormModal(false)}
                  className="p-2 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <X size={22} />
                </button>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="flex-grow overflow-y-auto modal-scrollbar p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Model Adı <span className="text-red-500">*</span></label>
                    <input type="text" name="name" id="name" value={currentForm.name} onChange={handleInputChange}
                           className={`w-full px-3 py-2 border ${formErrors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg shadow-sm focus:outline-none focus:ring-2 ${formErrors.name ? 'focus:ring-red-500' : 'focus:ring-blue-500 dark:focus:ring-blue-400'} bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100`} required />
                    {formErrors.name && <p className="mt-1 text-xs text-red-500">{formErrors.name}</p>}
                  </div>
                  <div>
                    <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Kategori <span className="text-red-500">*</span></label>
                    <input type="text" name="category" id="category" value={currentForm.category} onChange={handleInputChange}
                           className={`w-full px-3 py-2 border ${formErrors.category ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg shadow-sm focus:outline-none focus:ring-2 ${formErrors.category ? 'focus:ring-red-500' : 'focus:ring-blue-500 dark:focus:ring-blue-400'} bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100`} required />
                    {formErrors.category && <p className="mt-1 text-xs text-red-500">{formErrors.category}</p>}
                  </div>
                </div>
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Açıklama</label>
                  <textarea name="description" id="description" value={currentForm.description} onChange={handleInputChange} rows="3"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 min-h-[150px] max-h-[250px]"></textarea>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="model_year" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Model Yılı <span className="text-red-500">*</span></label>
                    <input type="date" name="model_year" id="model_year" value={currentForm.model_year} onChange={handleInputChange}
                           className={`w-full px-3 py-2 border ${formErrors.model_year ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg shadow-sm focus:outline-none focus:ring-2 ${formErrors.model_year ? 'focus:ring-red-500' : 'focus:ring-blue-500 dark:focus:ring-blue-400'} bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100`} required />
                    {formErrors.model_year && <p className="mt-1 text-xs text-red-500">{formErrors.model_year}</p>}
                  </div>
                  <div>
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Durum</label>
                    <select name="status" id="status" value={currentForm.status} onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                      <option value="Aktif">Aktif</option>
                      <option value="Geliştirme Aşamasında">Geliştirme Aşamasında</option>
                      <option value="Pasif">Pasif</option>
                      <option value="Arşivlendi">Arşivlendi</option>
                    </select>
                  </div>
                </div>
                <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center justify-between">
                    <span>Cihaz Görseli</span>
                    {imagePreviewUrl && (
                      <span className="text-xs text-blue-500 dark:text-blue-400">
                        {selectedFile ? selectedFile.name : "Harici görsel URL"}
                      </span>
                    )}
                  </label>

                  <div className="flex flex-col sm:flex-row items-center gap-4">
                    {/* Image Preview - Daha büyük önizleme */}
                    <div className="w-full sm:w-1/3 h-40 rounded-lg bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 flex items-center justify-center overflow-hidden">
                      {imagePreviewUrl ? (
                        <img 
                          src={imagePreviewUrl} 
                          alt="Önizleme" 
                          className="w-full h-full object-contain" 
                          onClick={() => imagePreviewUrl && window.open(imagePreviewUrl, '_blank')}
                          style={{ cursor: imagePreviewUrl ? 'zoom-in' : 'default' }}
                        />
                      ) : (
                        <Package size={32} className="text-gray-400 dark:text-gray-500" />
                      )}
                    </div>

                    <div className="flex-grow space-y-3 w-full sm:w-2/3">
                      {/* Dosya Yükleme Kontrolü */}
                      <div className="space-y-2">
                        {!imagePreviewUrl ? (
                          <div className="flex flex-col gap-2">
                            <label htmlFor="file-upload" className="cursor-pointer inline-flex items-center justify-center w-full px-4 py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none">
                              <input id="file-upload" name="file-upload" type="file" className="sr-only" ref={fileInputRef} onChange={handleFileChange} accept="image/png, image/jpeg, image/gif, image/webp" />
                              <span className="flex items-center">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                Görsel Yüklemek İçin Tıklayın veya Sürükleyin
                              </span>
                            </label>
                            
                            <div className="relative text-center">
                              <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-200 dark:border-gray-600"></div>
                              </div>
                              <div className="relative flex justify-center">
                                <span className="px-2 bg-white dark:bg-gray-800 text-sm text-gray-500 dark:text-gray-400">veya</span>
                              </div>
                            </div>
                            
                            <input
                              type="text"
                              name="image_url"
                              id="image_url"
                              value={currentForm.image_url || ''}
                              onChange={handleImageUrlChange}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                              placeholder="Görsel URL'sini buraya yapıştırın"
                              disabled={!!selectedFile}
                            />
                          </div>
                        ) : (
                          <div className="flex flex-col gap-3">
                            <div className="flex flex-wrap gap-2">
                              <button 
                                type="button"
                                onClick={handleRemoveImage}
                                className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50"
                              >
                                <Trash2 size={16} className="mr-1.5"/> Görseli Kaldır
                              </button>
                              
                              <button 
                                type="button"
                                onClick={() => {
                                  if (fileInputRef.current) {
                                    fileInputRef.current.click();
                                  }
                                }}
                                className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none"
                              >
                                <input id="file-upload-replace" name="file-upload" type="file" className="sr-only" ref={fileInputRef} onChange={handleFileChange} accept="image/png, image/jpeg, image/gif, image/webp" />
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                </svg>
                                Başka Görsel Yükle
                              </button>
                            </div>

                            {selectedFile && (
                              <div className="text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 p-2 rounded-md">
                                <span className="font-medium">Seçilen dosya:</span> {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                              </div>
                            )}
                            
                            {uploadingImage && (
                              <div className="flex items-center space-x-2 text-blue-500 dark:text-blue-400">
                                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>Görsel yükleniyor...</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Hata mesajları */}
                      {modelsError && modelsError.includes('Dosya yüklenemedi') && (
                        <p className="mt-1 text-xs text-red-500 bg-red-50 dark:bg-red-900/20 p-2 rounded-md">
                          <span className="font-bold">Hata:</span> {modelsError}
                        </p>
                      )}
                      
                      {/* Görsel Boyutu Bilgisi */}
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        <p>Önerilen görsel boyutu: 800x600px, maksimum 2MB</p>
                        <p>Desteklenen formatlar: PNG, JPEG, GIF, WEBP</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex justify-between items-center">
                    <h3 className="text-md font-semibold text-gray-800 dark:text-gray-100 mb-2">Gerekli Ürünler <span className="text-red-500">*</span></h3>
                    <button 
                        type="button" 
                        onClick={() => setShowProductSelectionModal(true)}
                        className="flex items-center text-sm bg-green-500 hover:bg-green-600 text-white font-medium py-1.5 px-3 rounded-lg transition duration-150">
                        <Plus size={16} className="mr-1.5" /> Ürün Ekle
                    </button>
                  </div>
                  {formErrors.components && <p className="text-xs text-red-500">{formErrors.components}</p>}
                  
                  {currentForm.components.length === 0 && (
                    <div className="text-center py-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                        <Package size={32} className="mx-auto text-gray-400 dark:text-gray-500" />
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Henüz ürün eklenmedi.</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500">Yukarıdaki 'Ürün Ekle' butonu ile başlayın.</p>
                    </div>
                  )}

                      <div className="max-h-[300px] overflow-y-auto pr-1 modal-scrollbar space-y-3">
                  {currentForm.components.map((component, index) => (
                    <motion.div 
                        key={`component-${component.productId}-${index}`}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.2 }}
                        className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600"
                    >
                      <span className="flex-grow text-sm text-gray-800 dark:text-gray-100 font-medium">
                        {component.productName || allProducts.find(p=>p.id === component.productId)?.name || 'Bilinmeyen Ürün'}
                      </span>
                      <div className="w-24">
                        <input 
                          type="number" 
                          value={component.quantity === null || component.quantity === undefined ? '' : component.quantity} 
                          min="0"
                          onChange={(e) => handleComponentChange(index, 'quantity', e.target.value)}
                          className={`w-full px-2 py-1 border ${formErrors.componentQuantities?.[index] ? 'border-red-500' : 'border-gray-300 dark:border-gray-500'} rounded-md text-sm focus:outline-none focus:ring-1 ${formErrors.componentQuantities?.[index] ? 'focus:ring-red-500' : 'focus:ring-blue-500'} bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 text-center`} 
                        />
                        {formErrors.componentQuantities?.[index] && <p className="mt-0.5 text-xs text-red-500">{formErrors.componentQuantities[index]}</p>}
                      </div>
                      <input 
                        type="text" 
                        value={component.unit === null || component.unit === undefined ? '' : component.unit} 
                        onChange={(e) => handleComponentChange(index, 'unit', e.target.value)}
                        className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-500 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 text-center" 
                        placeholder="Birim"
                      />
                      <button type="button" onClick={() => removeComponentByProductId(component.productId)}
                              className="p-1.5 rounded-full text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors">
                        <Trash2 size={16} />
                      </button>
                    </motion.div>
                  ))}
                      </div>
                </div>

                <div>
                  <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notlar</label>
                  <textarea name="notes" id="notes" value={currentForm.notes} onChange={handleInputChange} rows="2"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 min-h-[150px] max-h-[250px]"></textarea>
                </div>
              </form>

              <div className="flex items-center justify-end p-5 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl">
                <button type="button" onClick={() => setShowFormModal(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors shadow-sm">
                  İptal
                </button>
                <button
                  type="submit"
                  onClick={handleSubmit}
                  disabled={modelsLoading || uploadingImage} // Disable if saving model or uploading image
                      className={`flex items-center bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-150 ease-in-out ml-4 ${ (modelsLoading || uploadingImage) ? 'opacity-50 cursor-not-allowed' : '' }`}
                >
                  {/* Conditional Loading/Save Text */}
                  {(modelsLoading || uploadingImage) ? (
                     <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                       <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                       <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                     </svg>
                  ) : (
                     <Save size={18} className="mr-2" />
                  )}
                  <span>{uploadingImage ? 'Görsel Yükleniyor...' : (isEditing ? 'Değişiklikleri Kaydet' : 'Modeli Kaydet')}</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showDetailModal && selectedModel && (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md"
                onClick={() => setShowDetailModal(false)}
            >
                <motion.div
                initial={{ scale: 0.95, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.95, opacity: 0, y: 20 }}
                transition={{ type: "spring", stiffness: 260, damping: 25 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-white/30 to-indigo-100/30 dark:from-white/10 dark:to-indigo-900/10 backdrop-blur-md opacity-100 rounded-br-[24px] rounded-bl-[24px]">
                    <div className="flex items-center">
                        <Package size={24} className="text-blue-600 dark:text-blue-400 mr-3"/>
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {selectedModel.name} - Detaylar
                        </h2>
                    </div>
                    <button
                    onClick={() => setShowDetailModal(false)}
                        className="p-2 rounded-full text-gray-400 hover:bg-white/30 dark:hover:bg-gray-700/50 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                    <X size={22} />
                    </button>
                </div>

                <div className="flex-grow overflow-y-auto modal-scrollbar p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="md:col-span-1 relative group">
                                <div className="overflow-hidden rounded-lg shadow-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50">
                            <img 
                                src={selectedModel.image_url || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI0UwRTBFMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMThweCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgZmlsbD0iIzlFOUU5RSI+Q2loYXogTW9kZWxpPC90ZXh0Pjwvc3ZnPg=='} 
                                alt={selectedModel.name} 
                                        className="w-full h-auto max-h-[25vh] object-contain bg-gray-100 dark:bg-gray-700 aspect-video p-2"
                            />
                            <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowFullImageModal(true);
                                }}
                                        className="absolute bottom-2 right-2 p-2 bg-black/60 hover:bg-black/80 text-white rounded-full shadow-md transition-all duration-200 ease-in-out focus:outline-none group-hover:scale-110"
                                title="Tam boyutta görüntüle"
                            >
                                <Eye size={16} />
                            </button>
                        </div>
                            </div>
                            <div className="md:col-span-2 space-y-4 text-sm">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 shadow-sm border border-blue-100 dark:border-blue-800/50">
                                        <InfoItem label="Model Adı" value={selectedModel.name} icon={<Package size={16} className="text-blue-600 dark:text-blue-400"/>} />
                                    </div>
                                    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3 shadow-sm border border-purple-100 dark:border-purple-800/50">
                                        <InfoItem label="Kategori" value={selectedModel.category} icon={<Filter size={16} className="text-purple-600 dark:text-purple-400"/>} />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-3 shadow-sm border border-indigo-100 dark:border-indigo-800/50">
                                        <InfoItem label="Model Yılı" value={formatModelYear(selectedModel.model_year)} icon={<Info size={16} className="text-indigo-600 dark:text-indigo-400"/>} badgeColor="bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300"/>
                                    </div>
                                    <div className={`rounded-lg p-3 shadow-sm border ${
                                        selectedModel.status === 'Aktif' ? 'bg-green-50 dark:bg-green-900/20 border-green-100 dark:border-green-800/50' :
                                        selectedModel.status === 'Geliştirme Aşamasında' ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-100 dark:border-yellow-800/50' :
                                        'bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-800/50'
                                    }`}>
                            <InfoItem label="Durum" value={selectedModel.status} icon={
                                selectedModel.status === 'Aktif' ? <CheckCircle2 size={16} className="text-green-500"/> : 
                                selectedModel.status === 'Geliştirme Aşamasında' ? <RotateCcw size={16} className="text-yellow-500"/> :
                                <AlertCircle size={16} className="text-red-500"/>
                            } badgeColor={
                                selectedModel.status === 'Aktif' ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300' :
                                selectedModel.status === 'Geliştirme Aşamasında' ? 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300' :
                                'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300'
                            }/>
                                    </div>
                                </div>
                        </div>
                    </div>
                    
                        <div className="bg-white dark:bg-gray-800/60 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
                            <h3 className="text-md font-semibold text-gray-800 dark:text-white mb-3 flex items-center">
                                <MessageCircle className="w-5 h-5 mr-2 text-blue-500 dark:text-blue-400" />
                                Açıklama
                            </h3>
                            <div className="relative">
                                <div className={`text-gray-600 dark:text-gray-300 whitespace-pre-wrap bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg border border-gray-100 dark:border-gray-700 relative ${!showFullDescription && selectedModel.description && selectedModel.description.length > 300 ? 'max-h-[120px] overflow-hidden' : ''}`}>
                                    {selectedModel.description || 'Açıklama bulunmuyor.'}
                                    
                                    {!showFullDescription && selectedModel.description && selectedModel.description.length > 300 && (
                                        <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-gray-50 dark:from-gray-700/90 to-transparent"></div>
                                    )}
                                </div>
                                
                                {selectedModel.description && selectedModel.description.length > 300 && (
                                    <button 
                                        onClick={() => setShowFullDescription(!showFullDescription)}
                                        className="mt-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium text-sm flex items-center"
                                    >
                                        {showFullDescription ? (
                                            <>
                                                <ChevronUp size={16} className="mr-1" />
                                                Daha az göster
                                            </>
                                        ) : (
                                            <>
                                                <ChevronDown size={16} className="mr-1" />
                                                Devamını oku
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800/60 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
                            <h3 className="text-md font-semibold text-gray-800 dark:text-white mb-3 flex items-center">
                                <ShoppingCart className="w-5 h-5 mr-2 text-green-500 dark:text-green-400" />
                                Gerekli Ürünler
                            </h3>
                        {selectedModel.components && selectedModel.components.length > 0 ? (
                            <div className="space-y-2 bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
                                {selectedModel.components
                                  .slice(0, showAllComponents ? selectedModel.components.length : 5)
                                  .map((comp, idx) => {
                                    // Ürünün stok bilgisini al
                                    const productStock = allProducts.find(p => p.id === comp.productId)?.stock || 0;
                                    const requiredAmount = comp.quantity || 0;
                                    const hasEnoughStock = productStock >= requiredAmount;
                                    
                                    return (
                                      <div 
                                        key={idx} 
                                        className={`flex justify-between items-center p-2.5 bg-white dark:bg-gray-700 rounded-md border ${
                                          hasEnoughStock 
                                            ? 'border-gray-200 dark:border-gray-600' 
                                            : 'border-red-300 dark:border-red-700'
                                        } hover:shadow-md transition-shadow ${
                                          !hasEnoughStock ? 'bg-red-50 dark:bg-red-900/20' : ''
                                        }`}
                                      >
                                        <span className={`font-medium ${
                                          hasEnoughStock 
                                            ? 'text-gray-800 dark:text-gray-100' 
                                            : 'text-red-700 dark:text-red-300'
                                        }`}>
                                          {comp.productName}
                                          {!hasEnoughStock && (
                                            <span className="text-xs ml-2 text-red-600 dark:text-red-400">
                                              (Stok Yetersiz!)
                                            </span>
                                          )}
                                        </span>
                                        <div className="flex items-center space-x-3">
                                          <span className={`px-2 py-0.5 rounded-md text-xs ${
                                            hasEnoughStock 
                                              ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' 
                                              : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                                          }`}>
                                            {productStock}/{requiredAmount}
                                          </span>
                                          <span className={`${
                                            hasEnoughStock 
                                              ? 'text-gray-600 dark:text-gray-300' 
                                              : 'text-red-600 dark:text-red-300 font-medium'
                                          }`}>
                                            {comp.quantity} {comp.unit}
                                          </span>
                            </div>
                        </div>
                                    );
                                  })}

                                  {selectedModel.components.length > 5 && (
                                    <button 
                                      onClick={() => setShowAllComponents(!showAllComponents)}
                                      className="w-full mt-3 py-2 px-3 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-md text-sm font-medium transition-colors flex items-center justify-center"
                                    >
                                      {showAllComponents ? (
                                        <>
                                          <ChevronUp size={16} className="mr-1.5" />
                                          Daha az göster
                                        </>
                                      ) : (
                                        <>
                                          <ChevronDown size={16} className="mr-1.5" />
                                          Devamını göster ({selectedModel.components.length - 5} ürün daha)
                                        </>
                                      )}
                                    </button>
                                  )}
                              </div>
                            ) : (
                            <p className="text-gray-500 dark:text-gray-400 italic bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg border border-gray-100 dark:border-gray-700">Bu model için ürün reçetesi tanımlanmamış.</p>
                            )}
                        </div>

                    {selectedModel.notes && (
                            <div className="bg-white dark:bg-gray-800/60 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
                                <h3 className="text-md font-semibold text-gray-800 dark:text-white mb-3 flex items-center">
                                    <BookOpen className="w-5 h-5 mr-2 text-amber-500 dark:text-amber-400" />
                                    Ek Notlar
                                </h3>
                                <div className="relative">
                                    <div className={`text-gray-600 dark:text-gray-300 whitespace-pre-wrap bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg border border-gray-100 dark:border-gray-700 relative ${!showFullNotes && selectedModel.notes.length > 200 ? 'max-h-[120px] overflow-hidden' : ''}`}>
                                        {selectedModel.notes}
                                        
                                        {!showFullNotes && selectedModel.notes.length > 200 && (
                                            <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-gray-50 dark:from-gray-700/90 to-transparent"></div>
                                        )}
                                    </div>
                                    
                                    {selectedModel.notes.length > 200 && (
                                        <button 
                                            onClick={() => setShowFullNotes(!showFullNotes)}
                                            className="mt-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium text-sm flex items-center"
                                        >
                                            {showFullNotes ? (
                                                <>
                                                    <ChevronUp size={16} className="mr-1" />
                                                    Daha az göster
                                                </>
                                            ) : (
                                                <>
                                                    <ChevronDown size={16} className="mr-1" />
                                                    Devamını oku
                                                </>
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="bg-white dark:bg-gray-800/60 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
                            <h3 className="text-md font-semibold text-gray-800 dark:text-white mb-3 flex items-center">
                                <History className="w-5 h-5 mr-2 text-purple-500 dark:text-purple-400" />
                                Sistem Bilgileri
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
                            <InfoItem 
                                label="Oluşturan" 
                                value={selectedModel.created_by || "Belirtilmemiş"} 
                                icon={<User size={16} className="text-blue-500"/>} 
                            />
                            <InfoItem 
                                label="Oluşturma Tarihi" 
                                value={formatDate(selectedModel.created_at)} 
                                icon={<Calendar size={16} className="text-green-500"/>} 
                            />
                            <InfoItem 
                                label="Son Güncelleyen" 
                                value={selectedModel.updated_by || selectedModel.created_by || "Belirtilmemiş"} 
                                icon={<User size={16} className="text-orange-500"/>} 
                            />
                            <InfoItem 
                                label="Son Güncelleme Tarihi" 
                                value={formatDate(selectedModel.updated_at)} 
                                icon={<Clock size={16} className="text-purple-500"/>} 
                            />
                        </div>
                        </div>
                </div>

                    <div className="flex items-center justify-end p-5 border-t border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800/80 dark:to-gray-800 rounded-b-xl space-x-3">
                        <button 
                          type="button" 
                          onClick={() => handleEdit(selectedModel)}
                          className="flex items-center bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-150 ease-in-out"
                        >
                        <Edit2 size={18} className="mr-2" /> Düzenle
                    </button>
                    <button type="button" onClick={() => setShowDetailModal(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors shadow-sm ">
                    Kapat
                    </button>
                </div>
                </motion.div>
            </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showDeleteModal && selectedModel && (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                onClick={() => setShowDeleteModal(false)}
            >
                <motion.div
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                transition={{ type: "spring", stiffness: 300, damping: 25 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md"
                onClick={(e) => e.stopPropagation()}
                >
                <div className="p-6">
                    <div className="flex items-center justify-center mb-5">
                        <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                            <AlertCircle size={32} className="text-red-600 dark:text-red-400" />
                        </div>
                    </div>
                    <h2 className="text-xl font-semibold text-center text-gray-900 dark:text-white mb-2">
                        Modeli Sil
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-6">
                        '<span className="font-medium">{selectedModel.name}</span>' modelini silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
                    </p>
                </div>
                <div className="flex items-center justify-center p-5 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl space-x-3">
                    <button type="button" onClick={() => setShowDeleteModal(false)}
                            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors shadow-sm">
                    İptal
                    </button>
                    <button type="button" onClick={confirmDelete}
                            className="flex-1 flex items-center justify-center bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 px-4 rounded-lg shadow-md transition duration-150 ease-in-out">
                        <Trash2 size={18} className="mr-2" /> Sil
                    </button>
                </div>
                </motion.div>
            </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showFullImageModal && selectedModel && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowFullImageModal(false)}
          >
            <motion.div
              className="relative max-w-4xl max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
            >
              <img 
                src={selectedModel.image_url || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI0UwRTBFMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMThweCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgZmlsbD0iIzlFOUU5RSI+Q2loYXogTW9kZWxpPC90ZXh0Pjwvc3ZnPg=='} 
                alt={selectedModel.name} 
                className="max-w-full max-h-[85vh] object-contain shadow-2xl rounded-lg"
              />
              <button
                onClick={() => setShowFullImageModal(false)}
                className="absolute top-4 right-4 p-2 rounded-full text-white bg-black/60 hover:bg-black/80 transition-colors"
              >
                <X size={22} />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showProductSelectionModal && (
          <motion.div
            className="fixed inset-0 z-[55] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowProductSelectionModal(false)}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.9, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                <div className="flex items-center">
                    <ShoppingCart size={20} className="mr-2 text-blue-600 dark:text-blue-400" />
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Cihaza Ürün Ekle</h2>
                </div>
                <button onClick={() => setShowProductSelectionModal(false)} className="p-2 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                  <X size={22} />
                </button>
              </div>

              <div className="p-4 flex-shrink-0 border-b border-gray-200 dark:border-gray-700">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className={`w-5 h-5 ${componentSearchTerm ? 'text-blue-500 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`} />
                  </div>
                  <input
                    type="text"
                    placeholder="Ürün adı veya açıklama ile ara..."
                    value={componentSearchTerm}
                    onChange={(e) => setComponentSearchTerm(e.target.value)}
                    className={`w-full pl-10 pr-10 py-2.5 border ${componentSearchTerm ? 'border-blue-300 dark:border-blue-600' : 'border-gray-300 dark:border-gray-600'} rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-colors`}
                  />
                  {componentSearchTerm && (
                    <button 
                      onClick={() => setComponentSearchTerm('')} 
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>
              </div>

              <ul className="flex-grow overflow-y-auto modal-scrollbar p-4 space-y-3">
                {productsLoading ? (
                  <li className="py-10 text-center text-gray-500 dark:text-gray-400"> {/* Loading Spinner */} </li>
                ) : filteredProductsForSelector.length > 0 ? (
                  filteredProductsForSelector.map(product => (
                    <li key={product.id} className="flex items-center space-x-4 p-3 bg-gray-50 dark:bg-gray-700/70 rounded-lg border border-gray-200 dark:border-gray-600 hover:shadow-md transition-shadow">
                      <img 
                        src={product.image_url || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiBmaWxsPSIjRTBFMEUwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxMHB4IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIiBmaWxsPSIjOUU5RTlFIj5VcnVuPC90ZXh0Pjwvc3ZnPg=='} 
                        alt={product.name} 
                        className="w-16 h-16 rounded-md object-cover bg-gray-200 dark:bg-gray-600 flex-shrink-0"
                      />
                      <div className="flex-grow min-w-0">
                        <p className={`font-semibold text-gray-800 dark:text-gray-100 text-md truncate ${product.searchMatches?.includes('name') && componentSearchTerm ? 'bg-yellow-100 dark:bg-yellow-800/40 px-1 rounded' : ''}`}>
                          {product.name}
                        </p>
                        <p className={`text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2 ${product.searchMatches?.includes('description') && componentSearchTerm ? 'bg-yellow-100 dark:bg-yellow-800/40 px-1 rounded' : ''}`}>
                          {product.description || 'Açıklama yok.'}
                        </p>
                        <p className="text-xs text-blue-500 dark:text-blue-400 font-medium mt-1">Stok: {product.stock || 0}</p>
                      </div>
                      
                      <div className="flex flex-col items-center space-y-2 ml-auto flex-shrink-0 w-32">
                        {product.isAdded ? (
                          <div className="flex flex-col items-center w-full">
                            <span className="text-xs text-green-600 dark:text-green-400 mb-1">Ekli ({product.currentQuantityInRecipe} Adet)</span>
                            <button
                              type="button"
                              onClick={() => removeComponentByProductId(product.id)}
                              className="w-full flex items-center justify-center text-sm bg-red-500 hover:bg-red-600 text-white font-semibold py-1.5 px-3 rounded-md transition duration-150 ease-in-out"
                            >
                              <Trash2 size={16} className="mr-1.5" /> Çıkar
                            </button>
                          </div>
                        ) : (
                          <>
                            <input 
                              type="number" 
                              value={modalProductQuantities[product.id] ? modalProductQuantities[product.id] : '1'} 
                              onChange={(e) => handleModalQuantityChange(product.id, e.target.value)} 
                              min="1"
                              className="w-full text-center px-2 py-1 border border-gray-300 dark:border-gray-500 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 mb-1.5"
                            />
                            <button
                              type="button"
                              onClick={() => addComponent(product, modalProductQuantities[product.id] || 1)}
                              className="w-full flex items-center justify-center text-sm bg-green-500 hover:bg-green-600 text-white font-semibold py-1.5 px-3 rounded-md transition duration-150 ease-in-out"
                            >
                              <Plus size={16} className="mr-1.5" /> Ekle
                            </button>
                          </>
                        )}
                      </div>
                    </li>
                  ))
                ) : (
                  <li className="py-10 text-center text-gray-500 dark:text-gray-400">{/* No product found message */}</li>
                )}
              </ul>

              <div className="flex items-center justify-end p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl flex-shrink-0">
                 <button type="button" onClick={() => setShowProductSelectionModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors shadow-sm">
                  Kapat
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
        </>
      )}
    </div>
  );
};

const InfoSection = ({ title, children }) => (
    <div className="pt-4 border-t border-gray-200 dark:border-gray-600 first-of-type:border-t-0 first-of-type:pt-0">
      <h3 className="text-md font-semibold text-gray-800 dark:text-gray-100 mb-2">{title}</h3>
      {children}
    </div>
  );
  
  const InfoItem = ({ label, value, icon, badgeColor }) => (
    <div className="flex items-start">
      <span className="text-gray-500 dark:text-gray-400 w-8 h-8 flex-shrink-0 flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded-lg mr-3">
        {icon || <Info size={16}/>}
      </span>
      <div>
        <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">{label}</p>
        {badgeColor ? (
          <span className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${badgeColor}`}>
            {value}
          </span>
        ) : (
          <p className="text-gray-800 dark:text-gray-100 font-medium">{value || '-'}</p>
        )}
      </div>
    </div>
  );

export default MakineMontajListesi; 
